import { $fetch } from '@/boot/fetch'
import { replacePathTokens } from '@/utils/routes.util'

const ENDPOINT = '/retailers/{{retailerId}}/tour-status'

/**
 * Map the composable's camelCase tour payload onto the snake_case wire
 * contract expected by the GET/PATCH /tour-status endpoints (PEG-694).
 * Only defined keys are forwarded so partial updates stay partial.
 * @param {Object} payload
 * @returns {Object}
 */
function toWire(payload = {}) {
  const map = {
    tourName: 'tour_name',
    completionType: 'completion_type',
    skippedAtStep: 'skipped_at_step',
    skippedAtPhase: 'skipped_at_phase',
    totalSteps: 'total_steps',
  }
  const body = {}
  for (const [camel, snake] of Object.entries(map)) {
    // Drop null as well as undefined: a step without a phase yields a null
    // skippedAtPhase, and forwarding skipped_at_phase: null can fail backend
    // validation for what is an optional field. Omitted stays omitted.
    if (payload[camel] != null) body[snake] = payload[camel]
  }
  return body
}

/**
 * Map the snake_case GET /tour-status wire response onto the camelCase shape
 * the composable consumes. The backend returns snake_case (`has_completed`,
 * `completed_at`, ...) and `$fetch.peg` spreads `res.data` verbatim WITHOUT
 * camel-casing, so the composable would otherwise read `resp.hasCompleted` as
 * undefined and re-launch a completed tour whenever the localStorage gate is
 * absent (new browser/device/cleared storage). Normalizing here keeps the
 * server authoritative (PEG-609 AC18/AC20). Inverse of `toWire`.
 * @param {Object} data
 * @returns {Object}
 */
function fromWire(data = {}) {
  const map = {
    retailer_id: 'retailerId',
    tour_name: 'tourName',
    has_completed: 'hasCompleted',
    completed_at: 'completedAt',
    completion_type: 'completionType',
    skipped_at_step: 'skippedAtStep',
    skipped_at_phase: 'skippedAtPhase',
    total_steps: 'totalSteps',
  }
  const out = {}
  for (const [snake, camel] of Object.entries(map)) {
    if (snake in data) out[camel] = data[snake]
  }
  return out
}

export default {
  /**
   * @description Get the guided-tour completion status for a retailer.
   * Backs the GET /v1/retailers/:retailerId/tour-status endpoint (PEG-694).
   * @function get
   * @param {String} retailerId - The retailer ID
   * @param {Object} [opts] - Optional filters
   * @param {String} [opts.tourName] - Restrict to a single tour by name
   * @returns {Promise}
   */
  get(retailerId, { tourName } = {}) {
    const params = tourName ? { tour_name: tourName } : null

    // Default throwing behavior (per AGENTS.md): the caller, shouldShowTour(),
    // handles failures in its try/catch rather than checking response.success.
    // silent: true — shouldShowTour() is the single Rollbar logging owner for
    // this flow (richer context), so suppress $fetch.peg's own logging.
    //
    // Normalize the snake_case wire response to camelCase so the composable's
    // `resp.hasCompleted` check matches the server's `has_completed` field.
    return $fetch
      .peg(replacePathTokens(ENDPOINT, { retailerId }), {
        params,
        silent: true,
      })
      .then(res => ({ ...res, ...fromWire(res) }))
  },

  /**
   * @description Update the guided-tour status (mark complete or record a skip).
   * Backs the PATCH /v1/retailers/:retailerId/tour-status endpoint (PEG-694).
   * @function update
   * @param {String} retailerId - The retailer ID
   * @param {Object} payload - { tourName, completionType, skippedAtStep, skippedAtPhase, totalSteps }
   * @returns {Promise}
   */
  update(retailerId, payload) {
    // Keep $fetch.peg's default throwing behavior: persistStatus() relies on a
    // rejected promise to skip the localStorage gate on a failed PATCH, so a
    // transient API error can't permanently suppress the tour client-side.
    // silent: true — persistStatus() is the single Rollbar logging owner here
    // (logs retailerId + completionType), so suppress $fetch.peg's own logging.
    return $fetch.peg(replacePathTokens(ENDPOINT, { retailerId }), {
      method: 'PATCH',
      body: toWire(payload),
      silent: true,
    })
  },
}
